import axios, { AxiosInstance } from 'axios';
import { getType } from 'typesafe-actions';

import store from '../../app/store';
// Cookie-based auth only. No token storage in JS. No Authorization header needed.
import { spinnerStart, spinnerStop } from '../actions/spinner.actions';
import { logoutActions } from '../../features/account/login/login.actions';
import { showNotification } from '../actionCreators/dispatchNotification';


export const startSpinner = () => {
  store.dispatch({ type: getType(spinnerStart) });
}

export const stopSpinner = () => {
  store.dispatch({ type: getType(spinnerStop) });
}

function apiClient(): AxiosInstance {
  let client = axios.create({
    baseURL: "/api/",
    timeout: 120000,
    headers: {
      'Content-Type': 'application/json'//,
    },
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    withCredentials: true,  // Ensure cookies (including HttpOnly auth cookie) are sent with requests
    // For same-origin requests, cookies are sent automatically, but this ensures it works for cross-origin too
    // (when CORS allows credentials)
  });

  client.interceptors.request.use(async (config) => {
    await startSpinner();
    // SECURITY: Do not send Authorization header - authentication is via HttpOnly cookie (MHA_JWT_AUTH)
    // Cookies are automatically sent by browser for same-origin requests
    // XSRF token header is handled automatically by Axios (xsrfCookieName/xsrfHeaderName config)

    return config;
  }, error => {
    return Promise.reject(error);
  });

  client.interceptors.response.use(
    async response => {
      if (response.status === 401) {
        // Don't trigger logout on 401 from /account/current-token - it's expected when not authenticated
        // This endpoint is used for auth initialization and may legitimately return 401
        const url = response.config?.url || '';
        if (!url.includes('/account/current-token')) {
          store.dispatch({ type: getType(logoutActions.request) });
          showNotification('Your session timed out.', 'error');
        }
      }
      setTimeout(async () => { await stopSpinner(); }, 500);
      return response
    },
    async error => {
      if (error.code === 'ECONNABORTED') {
        showNotification('The request timed out. Please try again.', 'error');
      } else if (error.response?.status === 401) {
        // Don't re-trigger logout from account management endpoints:
        // - /account/current-token: legitimately returns 401 when not authenticated
        // - /account/logout: would cause an infinite loop (logout 401 → dispatch logout → repeat)
        // - /account/refresh-token: a 401 here means refresh failed; let the caller handle it
        const url = error.config?.url || '';
        const noLogoutUrls = ['/account/current-token', '/account/logout', '/account/refresh-token'];
        if (!noLogoutUrls.some(u => url.includes(u))) {
          store.dispatch({ type: getType(logoutActions.request) });
          showNotification('Your session timed out.', 'error');
        }
      }
      await stopSpinner();
      return Promise.reject(error);
    }
  );

  return client;
}



export default apiClient;
